import _axios from "axios";
import queryString from "query-string";
import { isEmpty } from "lodash";
import { axios } from "../lib";
import * as endpoints from "./apiEndpoints";
import { customToast as toast } from "../utils";

class ProjectService {
  static get(url) {
    return axios
      .get(`${endpoints.BASE_URL}${endpoints.PROJECTS}${url}`)
      .then((res) => res.data)
      .catch((err) => {
        toast.error(err.message);
      });
  }

  static put(url) {
    return axios
      .put(`${endpoints.BASE_URL}${endpoints.PROJECTS}${url}`)
      .then((res) => res.data)
      .catch((err) => {
        toast.error(err.message);
      });
  }

  static loadProjects(body) {
    let controller;

    return (() => {
      if (controller) {
        controller.abort();
      }

      controller = new AbortController();

      let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}`;

      if (body && typeof body.page !== "undefined") {
        return axios
          .post(url, body, { signal: controller.signal })
          .then((response) => response.data);
      }

      if (body) {
        const params = queryString.stringify(body);
        url = `${url}?${params}`;
      }

      return axios
        .get(url, { signal: controller.signal })
        .then((response) => response.data);
    })();
  }

  static loadDepartmentProjects(filters) {
    let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/loadDepartmentProjects`;

    if (filters && filters.filtered && !isEmpty(filters.filtered)) {
      const params = `&${queryString.stringify(
        { ...filters.filtered, ...filters.sorted },

        { arrayFormat: "bracket" }
      )}`;

      url = url.concat(`?${params}`);
    }

    return axios.get(url).then((response) => response.data);
  }

  static loadProjectEmployeeSummary(filters) {
    let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/loadProjectEmployeeSummary`;

    if (filters && filters.filtered && !isEmpty(filters.filtered)) {
      const params = `&${queryString.stringify(
        { ...filters.filtered, ...filters.sorted },

        { arrayFormat: "bracket" }
      )}`;

      url = url.concat(`?${params}`);
    }

    return axios.get(url).then((response) => response.data);
  }

  static loadProjectSummary(filters) {
    let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/loadProjectSummary`;

    if (filters && filters.filtered && !isEmpty(filters.filtered)) {
      const params = `&${queryString.stringify(
        { ...filters.filtered, ...filters.sorted },

        { arrayFormat: "bracket" }
      )}`;

      url = url.concat(`?${params}`);
    }

    return axios.get(url).then((response) => response.data);
  }

  static getProject(id) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}`;
    return axios.get(url).then((response) => response.data);
  }

  static fetchProjectList(params) {
    const query = new URLSearchParams();

    // eslint-disable-next-line no-restricted-syntax
    for (const k in params) {
      if (Array.isArray(params[k])) {
        query.append(k, params[k].join(","));
      } else {
        query.append(k, params[k]);
      }
    }
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}?${query}`;
    return axios.get(url).then((res) => res.data);
  }

  static createProject(project) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/new`;
    const body = {
      project
    };

    return axios.post(url, body).then((response) => response.data);
  }

  static updateProject(project) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${project.id}`;
    const body = {
      project
    };

    return axios.put(url, body).then((response) => response.data);
  }

  static updateProjectWithNoSideEffects(id, project) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/pure-update/${id}`;

    return axios.put(url, project).then((response) => response.data);
  }

  static deleteProject(id) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}`;
    return axios.delete(url).then((response) => response.data);
  }

  static createTask(id, task) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/task`;
    const body = {
      task
    };

    return axios.post(url, body).then((response) => response.data);
  }

  static updateTask(id, task) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/task`;
    const body = {
      task
    };

    return axios.put(url, body).then((response) => response.data);
  }

  static deleteTask(id, taskId) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/task/${taskId}`;
    return axios.delete(url).then((response) => response.data);
  }

  static createNotification(id, notification) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/notification`;
    const body = {
      notification
    };

    return axios.post(url, body).then((response) => response.data);
  }

  static updateNotification(id, notification) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/notification`;
    const body = {
      notification
    };

    return axios.put(url, body).then((response) => response.data);
  }

  static deleteNotification(id, notificationId) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/notification/${notificationId}`;
    return axios.delete(url).then((response) => response.data);
  }

  static createNote(id, note) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/note`;
    const body = {
      note
    };

    return axios.post(url, body).then((response) => response.data);
  }

  static updateNote(id, note) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/note`;
    const body = {
      note
    };

    return axios.put(url, body).then((response) => response.data);
  }

  static deleteNote(id, noteId) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${id}/note/${noteId}`;
    return axios.delete(url).then((response) => response.data);
  }

  static uploadFile(project, file, isSOW) {
    const formData = new FormData();
    formData.append("projectUpload", file);

    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${project.id}/upload`;

    return axios
      .post(url, formData)
      .then((response) => {
        if (isSOW) {
          this.updateProjectWithNoSideEffects(project.id, {
            sowAttachmentUrl: response.data.fileName
          });
        } else {
          this.createAttachment(project, response.data);
        }
      })
      .then((resp) => resp);
  }

  static createAttachment(project, attachment) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${project.id}/attachment`;
    const body = {
      attachment: {
        name: attachment.name,
        user: attachment.user,
        path: attachment.fileName
      }
    };

    return axios.post(url, body).then((response) => response.data);
  }

  static downloadFile(project, attachment) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${project.id}/attachment/${attachment.path}`;

    return axios.get(url).then((response) => {
      // simulate DOM click for download <- doi
      const file_path = response.data.url;
      const a = document.createElement("A");
      a.href = file_path;
      a.target = "_blank;";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      return response.data;
    });
  }

  static updateAttachment(project, attachment) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${project.id}/attachment`;
    const body = {
      attachment
    };

    return axios.put(url, body).then((response) => response.data);
  }

  static deleteAttachment(customer, attachment) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/${customer.id}/attachment/delete`;
    const body = {
      attachment
    };

    return axios.post(url, body).then((response) => response.data);
  }

  static getBudgetConsumed(id) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/budget/${id}`;
    return axios.get(url).then((response) => response.data);
  }

  static findByNameAndCustomers(search, customer) {
    let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/search`;
    const params = `&${queryString.stringify(
      {
        search,
        customer,
        "sort.name": 1
      },
      { arrayFormat: "bracket" }
    )}`;
    url = url.concat(`?${params}`);
    return axios.get(url).then((response) => response.data);
  }

  static byCustomer(customer) {
    let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/byCustomer`;
    const params = `&${queryString.stringify(
      { customer },
      { arrayFormat: "bracket" }
    )}`;
    url = url.concat(`?${params}`);
    return axios.get(url).then((response) => response.data);
  }

  static findByNumber(project_number) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/search-number?project_number=${project_number}`;
    return axios.get(url).then((response) => response.data);
  }

  static cloneProject(project) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/clone`;
    const body = {
      project
    };
    return axios.post(url, body).then((response) => response.data);
  }

  static updateKeywordsAndRequestNatures(projectId, data) {
    return axios
      .put(
        `${endpoints.BASE_URL}${endpoints.PROJECTS}/${projectId}/keywords-request-natures`,
        data
      )
      .then((res) => res.data)
      .catch((err) => {
        toast.error(err.message);
      });
  }

  static loadProjectsWithSupportResources(params) {
    const url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/with-support-resources?${params}`;
    return axios.get(url).then((response) => response.data);
  }

  static updateProjectSupportResources(projectId, data) {
    return axios
      .put(
        `${endpoints.BASE_URL}${endpoints.PROJECTS}/${projectId}/support-resources`,
        data
      )
      .then((res) => res.data)
      .catch((err) => {
        toast.error(err.message);
      });
  }

  static loadActiveProjects(filters) {
    let url = `${endpoints.BASE_URL}${endpoints.PROJECTS}/active`;
    if (!isEmpty(filters)) {
      const params = `&${queryString.stringify(filters, {
        arrayFormat: "bracket"
      })}`;
      url = url.concat(`?${params}`);
    }

    return axios.get(url).then((response) => response.data);
  }
}

export default ProjectService;
